/**
 * Hook for extracting and caching asset metadata (dimensions, duration)
 * Uses client-side extraction with in-memory caching
 */

import { useState, useEffect } from "react";
import {
  Asset,
  getAssetType,
  AssetDimensions,
  AssetMetadata,
} from "@/types/asset.types";

// In-memory cache for current session
const metadataCache = new Map<string, AssetMetadata>();
const pendingExtractions = new Map<string, Promise<AssetMetadata | null>>();

/**
 * Extract metadata from an image URL
 */
async function extractImageMetadata(url: string): Promise<AssetMetadata> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Note: No crossOrigin needed - we only read naturalWidth/naturalHeight

    img.onload = () => {
      resolve({
        dimensions: {
          width: img.naturalWidth,
          height: img.naturalHeight,
          aspectRatio: img.naturalWidth / img.naturalHeight,
        },
      });
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for metadata extraction"));
    };

    img.src = url;
  });
}

/**
 * Extract metadata from a video URL (dimensions and duration only)
 */
async function extractVideoMetadata(url: string): Promise<AssetMetadata> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;

    let hasResolved = false;

    const cleanup = () => {
      video.src = "";
      video.load();
    };

    video.onloadedmetadata = () => {
      if (hasResolved) return;
      hasResolved = true;

      const dimensions: AssetDimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: video.videoWidth / video.videoHeight,
      };

      cleanup();
      resolve({
        dimensions,
        duration: video.duration,
      });
    };

    video.onerror = () => {
      if (!hasResolved) {
        hasResolved = true;
        cleanup();
        resolve({});
      }
    };

    // Timeout after 10 seconds
    setTimeout(() => {
      if (!hasResolved) {
        hasResolved = true;
        cleanup();
        resolve({});
      }
    }, 10000);

    video.src = url;
  });
}

/**
 * Extract metadata based on asset type
 */
async function extractMetadata(asset: Asset): Promise<AssetMetadata | null> {
  const assetType = getAssetType(asset.mimeType);

  switch (assetType) {
    case "image":
      return extractImageMetadata(asset.downloadUrl);
    case "video":
      return extractVideoMetadata(asset.downloadUrl);
    default:
      return null;
  }
}

/**
 * Hook to extract and cache asset metadata
 * @param asset - The asset to extract metadata for
 * @returns Object with metadata, loading state, and error
 */
export function useAssetMetadata(asset: Asset | null) {
  const [metadata, setMetadata] = useState<AssetMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!asset) {
      setMetadata(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Check cache first
    const cached = metadataCache.get(asset.id);
    if (cached) {
      setMetadata(cached);
      setIsLoading(false);
      return;
    }

    // Check if already extracting
    const pending = pendingExtractions.get(asset.id);
    if (pending) {
      setIsLoading(true);
      pending
        .then((meta) => {
          if (meta) setMetadata(meta);
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Start extraction
    setIsLoading(true);
    setError(null);

    const extraction = extractMetadata(asset);
    pendingExtractions.set(asset.id, extraction);

    extraction
      .then((meta) => {
        if (meta) {
          metadataCache.set(asset.id, meta);
          setMetadata(meta);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        setIsLoading(false);
        pendingExtractions.delete(asset.id);
      });
  }, [asset?.id, asset?.downloadUrl]);

  return { metadata, isLoading, error };
}

/**
 * Get cached metadata synchronously (useful for lists)
 */
export function getCachedMetadata(assetId: string): AssetMetadata | null {
  return metadataCache.get(assetId) || null;
}

/**
 * Prefetch metadata for multiple assets
 */
export async function prefetchAssetMetadata(
  assets: Asset[],
): Promise<Map<string, AssetMetadata>> {
  const results = new Map<string, AssetMetadata>();

  await Promise.all(
    assets.map(async (asset) => {
      // Skip if already cached
      if (metadataCache.has(asset.id)) {
        const cached = metadataCache.get(asset.id)!;
        results.set(asset.id, cached);
        return;
      }

      // Skip if already extracting
      if (pendingExtractions.has(asset.id)) {
        const meta = await pendingExtractions.get(asset.id);
        if (meta) results.set(asset.id, meta);
        return;
      }

      // Extract metadata
      const meta = await extractMetadata(asset);
      if (meta) {
        metadataCache.set(asset.id, meta);
        results.set(asset.id, meta);
      }
    }),
  );

  return results;
}
